import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  Stack,
  Link,
  TextField,
  Button,
  Chip,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  YouTube,
  LocationOn,
  Phone,
  Email,
  Send,
  ArrowUpward,
  Language,
  Verified,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showScroll, setShowScroll] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const resources = [
    { name: 'FAQ', path: '/faqs' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  const locations = [
    {
      country: 'SOUTH AFRICA',
      flag: '🇿🇦',
      address: '341 Beyers Naude Drive, Northcliff Johannesburg, South Africa, 2195',
      phone: '+27 11 123 4567',
      email: 'info@worldwidecomm.co.za',
      hours: 'Mon-Fri: 9am - 6pm SAST',
      isMainHQ: true,
    },
    {
      country: 'ESWATINI',
      flag: '🇸🇿',
      address: 'Corner of Louw & Ngwane Street, Manzini, Eswatini',
      phone: '+268 2505 0888',
      email: 'info@worldwidecomm.co.sz',
      hours: 'Mon-Fri: 9am - 5pm SAST',
      isRegionalHQ: true,
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#191919',
        color: '#fefefe',
        position: 'relative',
        mt: 'auto',
      }}
    >
      {/* Main Footer */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Brand Section */}
          <Grid item xs={12} sm={6} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: '#e60108',
                    width: 36,
                    height: 36,
                  }}
                >
                  <Language sx={{ fontSize: 20, color: '#fefefe' }} />
                </Avatar>
                <Typography variant="h6" fontWeight="800" sx={{ color: '#e60108' }}>
                  Worldwide Comm
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ mb: 1.5, opacity: 0.7, display: 'block' }}>
                Global communication solutions connecting businesses across 150+ countries since 2005.
              </Typography>
              
              {/* Trust Badges */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip 
                  icon={<Verified sx={{ fontSize: 12 }} />} 
                  label="ISO 27001 Certified" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(230,1,8,0.15)', 
                    color: '#e60108',
                    height: 24,
                    '& .MuiChip-icon': { color: '#e60108', fontSize: 14 }
                  }} 
                />
                <Chip 
                  label="24/7 Support" 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fefefe', height: 24 }} 
                />
              </Stack>

              {/* Social Links */}
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  component="a"
                  href="https://facebook.com"
                  target="_blank"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fefefe',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#1877F2', transform: 'translateY(-2px)' },
                  }}
                >
                  <Facebook sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://twitter.com"
                  target="_blank"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fefefe',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#1DA1F2', transform: 'translateY(-2px)' },
                  }}
                >
                  <Twitter sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://linkedin.com"
                  target="_blank"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fefefe',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#0077B5', transform: 'translateY(-2px)' },
                  }}
                >
                  <LinkedIn sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://instagram.com"
                  target="_blank"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fefefe',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#E4405F', transform: 'translateY(-2px)' },
                  }}
                >
                  <Instagram sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  component="a"
                  href="https://youtube.com"
                  target="_blank"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#fefefe',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#FF0000', transform: 'translateY(-2px)' },
                  }}
                >
                  <YouTube sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={3} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#e60108', fontSize: '0.8rem' }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {quickLinks.map((link) => (
                  <Link
                    key={link.name}
                    component={RouterLink}
                    to={link.path}
                    underline="hover"
                    sx={{
                      color: 'rgba(254,254,254,0.7)',
                      fontSize: '0.75rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#e60108',
                        transform: 'translateX(3px)',
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          {/* Resources */}
          <Grid item xs={6} sm={3} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#e60108', fontSize: '0.8rem' }}>
                Resources
              </Typography>
              <Stack spacing={1}>
                {resources.map((link) => (
                  <Link
                    key={link.name}
                    component={RouterLink}
                    to={link.path}
                    underline="hover"
                    sx={{
                      color: 'rgba(254,254,254,0.7)',
                      fontSize: '0.75rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#e60108',
                        transform: 'translateX(3px)',
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          {/* Global Offices */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#e60108', fontSize: '0.8rem' }}>
                Our Offices
              </Typography>
              <Stack spacing={2}>
                {locations.map((location, idx) => (
                  <Box key={idx}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                        {location.flag}
                      </Typography>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold" 
                        sx={{ 
                          color: location.isMainHQ ? '#e60108' : location.isRegionalHQ ? '#FFC107' : '#fefefe',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {location.country}
                        {location.isMainHQ && (
                          <Chip 
                            label="HQ" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#e60108', 
                              color: '#fefefe', 
                              height: 14, 
                              fontSize: '0.45rem',
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.5 }
                            }} 
                          />
                        )}
                        {location.isRegionalHQ && (
                          <Chip 
                            label="Regional HQ" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#FFC107', 
                              color: '#191919', 
                              height: 14, 
                              fontSize: '0.45rem',
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.5 }
                            }} 
                          />
                        )}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5} sx={{ pl: 3 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <LocationOn sx={{ fontSize: 10, opacity: 0.7, mt: 0.2 }} />
                        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                          {location.address}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Phone sx={{ fontSize: 10, opacity: 0.7 }} />
                        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                          {location.phone}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Email sx={{ fontSize: 10, opacity: 0.7 }} />
                        <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                          {location.email}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.6rem' }}>
                          🕒 {location.hours}
                        </Typography>
                      </Stack>
                    </Stack>
                    {idx < locations.length - 1 && (
                      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
                    )}
                  </Box>
                ))}
              </Stack>
            </motion.div>
          </Grid>
        </Grid>

        {/* Newsletter Section - Compact */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#e60108', fontSize: '0.75rem' }}>
                Subscribe to Our Newsletter
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.65rem' }}>
                Get the latest updates on global connectivity solutions
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: 'rgba(255,255,255,0.05)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: '#e60108' },
                      '&.Mui-focused fieldset': { borderColor: '#e60108' },
                    },
                    '& .MuiInputBase-input': { color: '#fefefe', py: 0.75 },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  onClick={handleSubscribe}
                  endIcon={<Send sx={{ fontSize: 14 }} />}
                  sx={{
                    bgcolor: '#e60108',
                    fontSize: '0.65rem',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                    '&:hover': { bgcolor: '#b80006' },
                  }}
                >
                  Subscribe
                </Button>
              </Stack>
              {subscribed && (
                <Typography variant="caption" sx={{ color: '#00C853', mt: 0.5, display: 'block', fontSize: '0.6rem' }}>
                  ✓ Subscribed successfully!
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Bottom Bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.6rem' }}>
            © 2005 - {new Date().getFullYear()} Worldwide Communication. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Link component={RouterLink} to="/privacy" underline="hover" color="inherit" sx={{ opacity: 0.5, fontSize: '0.55rem' }}>
              Privacy
            </Link>
            <Link component={RouterLink} to="/terms" underline="hover" color="inherit" sx={{ opacity: 0.5, fontSize: '0.55rem' }}>
              Terms
            </Link>
            <Link component={RouterLink} to="/cookies" underline="hover" color="inherit" sx={{ opacity: 0.5, fontSize: '0.55rem' }}>
              Cookies
            </Link>
          </Stack>
        </Stack>
      </Container>

      {/* Scroll to Top Button */}
      <Collapse in={showScroll}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            bgcolor: '#e60108',
            color: '#fefefe',
            width: 40,
            height: 40,
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#b80006',
              transform: 'translateY(-3px)',
            },
          }}
        >
          <ArrowUpward sx={{ fontSize: 20 }} />
        </IconButton>
      </Collapse>
    </Box>
  );
};

export default Footer;